/**
 * sessionBlocks — the SIXTEEN session-block components (v0.9 `native-ir`).
 *
 * A session block is not a widget module. The model authors DATA between
 * `<vi-block>…</vi-block>`, the WAL reducer projects each envelope into ONE
 * `session_block` card whose `data.node` is exactly one of these nodes, and
 * both kits run that node through the interpreter as a NODE, never as a page.
 * The node is an IMMUTABLE SNAPSHOT OF ONE ANSWER: it owns no feed, no
 * identity, no CRUD and no selection that outlives the turn.
 *
 * THREE RULES THIS FILE IS WRITTEN UNDER, all from the PRD's §5 (Session
 * Design System), and all of them are the OPPOSITE of the widget lane:
 *
 *  1. SESSION HAS NO PERSONALITY. A widget owns a display face and an accent;
 *     a session owns neither. Every colour here comes from a `--vi-*` token,
 *     the accent IS the ink, the background is paper, and the only colour on
 *     the surface comes from the user's own photographs. Nothing in this file
 *     names a hex, a px font size or a shadow.
 *  2. THE DATA IS AUTHORED, NEVER BOUND. Every prop of every block is
 *     declared `nodeProps` in the registry, so the interpreter passes it
 *     through VERBATIM: a snapshot has no context to bind against, and a
 *     paragraph of model prose containing `{braces}` must survive as the
 *     words the user was shown rather than being read as a path.
 *  3. THE HOST OWNS LEAVING. Opening a picture full-screen, opening a URL,
 *     saving a file, starting a session — every one of those is a host
 *     capability reached through a declared signal. A block never navigates,
 *     never downloads, and never confirms a write it did not make.
 *
 * The blocks reuse `Image` (and through it the SEALED media rule: `media://`
 * never reaches the DOM — the api-server resolves every handle inside
 * `data.node` before the card ships, and an unresolved one degrades to the
 * plate rather than a broken picture) and `BeforeAfterCompare` for the one
 * block that is two pictures.
 */
import { useState } from 'react';
import { Image } from './primitives';
import { BeforeAfterCompare } from './media';
import { hitTarget, resetButton, SHIMMER_STYLE } from './ui';
import {
  ACTION_HOST_OP, blockGlyph, fileTypeGlyph, fileTypeLabel, mediaProgressLabel,
  workerProgressLabel,
} from '../sessionBlockVocab';

// ── the session type scale (§5.3), as style objects ────────────────────────
// Fixed steps, never clamped; weights only 800 / 700 / 650 / 400; leading 1.22
// for headings and 1.55 for prose, never past 1.6.
const T = {
  cardTitle: { fontSize: 'var(--vi-t-2xl)', fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.22 },
  h2: { fontSize: 'var(--vi-t-xl)', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.22 },
  h3: { fontSize: 'var(--vi-t-lg)', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.22 },
  body: { fontSize: 'var(--vi-t-base)', fontWeight: 400, lineHeight: 1.55 },
  small: { fontSize: 'var(--vi-t-sm)', fontWeight: 400, lineHeight: 1.5 },
  smallStrong: { fontSize: 'var(--vi-t-sm)', fontWeight: 650, lineHeight: 1.5 },
  label: {
    fontSize: 'var(--vi-t-xs)', fontWeight: 600, lineHeight: 1.2,
    textTransform: 'uppercase', letterSpacing: '0.07em',
  },
};

/**
 * The one block container (§5.4) — and it draws NO FRAME. **Do not restore the
 * surface / hairline / radius / shadow / padding here.**
 *
 * A SESSION IS ONE ANSWER, NOT A STACK OF OBJECTS. The html lane renders a turn
 * edge-to-edge with no plate around it, and `agent_speech` (§4) has always been
 * bare on purpose; a native-ir turn is the SAME answer authored as data, so it
 * reads the same way. A box around each block turned every paragraph into a
 * separate thing the reader has to re-enter — the plate, the 16px inset and the
 * surface's own gutter compounded into a ~60px indent step that the prose next
 * to it does not have.
 *
 * So this const is LAYOUT ONLY: the ink (blocks inherit the surface's paper)
 * and the clip that keeps a wide table scrolling in its own box instead of
 * scrolling the page sideways. Vertical rhythm belongs to the SURFACE, which
 * spaces one block-card from the next exactly as it spaces two paragraphs.
 *
 * The one exception is `ROW_CARD` below: a TAPPABLE row still has to look
 * tappable.
 */
const CARD = {
  color: 'var(--vi-text)',
  overflow: 'hidden',
};

/**
 * A tappable ROW's outline — `link_preview`'s rows, and only those.
 *
 * This is an AFFORDANCE, not a block frame: the row is a button, and on a
 * frameless surface a button with no edge at all is a "fake-tappable dead
 * card". It keeps the hairline (the same one `table` draws between rows and
 * `action_bar` draws around a secondary button) and no fill, so it reads as a
 * control on the paper rather than as a plate around the answer.
 */
const ROW_CARD = {
  border: '0.7px solid var(--vi-border)',
  borderRadius: 'var(--vi-r)',
  padding: 'var(--sp-3)',
  color: 'var(--vi-text)',
  overflow: 'hidden',
};

const COL = (gap = 'var(--sp-3)') => ({ display: 'flex', flexDirection: 'column', gap });

/** A block's optional heading — drawn only when it was authored. */
function BlockTitle({ text }) {
  if (!text) return null;
  return <div style={{ ...T.h2, color: 'var(--vi-text)' }}>{text}</div>;
}

/** …and its optional foot note, the one line every list block may carry. */
function BlockFooter({ text }) {
  if (!text) return null;
  return <div style={{ ...T.small, color: 'var(--vi-text-muted)' }}>{text}</div>;
}

const list = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (v == null ? '' : String(v));

// ── fire-and-continue media: the two states BEFORE the asset lands ─────────
//
// A generated picture or video is authored ONCE (P2-A) and the ref arrives
// later as a `replace_card`. Until then the block carries `slot` — the id the
// model fired the generation tool at — and NO ref field at all; a generation
// that fails carries `error`, a ready human sentence, instead. Neither state
// may draw a broken picture, so both draw the SAME plate the kit already uses
// for a picture that has not decoded: pending shimmers, failed holds the
// server's sentence. A block that carries neither is unchanged — `Image` draws
// its own fallback, which is what an authored-but-empty ref always did.

/** `ref` present → 'ready'; `error` → 'failed'; a bare `slot` → 'pending'. */
function mediaWait(ref, node = {}) {
  if (ref) return null;
  if (node.error) return 'failed';
  return node.slot ? 'pending' : null;
}

/**
 * The plate a still-generating (or failed) asset occupies. It holds the frame
 * the picture will fill, so the card does not jump when the ref lands.
 *
 * `stage` / `pct` are the `media_progress` DECORATION (CONTRACT §5) and nothing
 * more: the op is non-terminal, slot-keyed, and MAY NEVER ARRIVE — a picture
 * often resolves before the first poll tick. So it only ever swaps the one word
 * under the shimmer, and a plate with no stage is byte-for-byte the plate this
 * kit has always drawn. A stage this build does not know reads as plain
 * pending (`mediaProgressLabel` → null), never as a third state.
 */
function MediaWaitPlate({ state, error, ratio = '4/3', rounded = true, alt, stage, pct }) {
  const failed = state === 'failed';
  const waiting = mediaProgressLabel(stage, pct) || 'Generating…';
  return (
    <div
      role="img"
      data-media-state={failed ? 'failed' : 'pending'}
      aria-label={failed ? str(error) || 'could not be generated' : `${str(alt) || 'Media'} — ${waiting}`}
      data-media-stage={failed ? undefined : (stage || undefined)}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: str(ratio) || '4/3',
        borderRadius: rounded ? 'var(--vi-r-inner)' : 0,
        overflow: 'hidden',
        background: 'var(--vi-surface-muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--sp-4)', boxSizing: 'border-box',
      }}
    >
      {failed ? (
        <span style={{ ...T.small, color: 'var(--vi-text-muted)', textAlign: 'center' }}>
          {str(error) || 'This one could not be generated.'}
        </span>
      ) : (
        <>
          <span aria-hidden style={{ ...SHIMMER_STYLE, position: 'absolute', inset: 0 }} />
          {/* the html path says "🖼️ Generating…" here; a session block says the
              same thing without the emoji — a block is monochrome. A live
              `media_progress` stage replaces the word and nothing else. */}
          <span style={{ ...T.label, color: 'var(--vi-text-muted)', position: 'relative' }}>
            {waiting}
          </span>
        </>
      )}
    </div>
  );
}

// ── the THUMBNAIL column (B18 P-A) ─────────────────────────────────────────
//
// A search tool answers with a per-product `image_url` on a PUBLIC CDN
// (`encrypted-tbn*.gstatic.com/shopping`), and the model carries it into the
// block verbatim. That is the one media on this surface that is NOT a
// `media://` handle: it is an external address the answer quotes, so it is
// rendered straight through `Image` (network `<img>` + the kit's own
// shimmer-then-fallback) and never routed through the fill pipeline.
//
// THE COLUMN IS ALL-OR-NOTHING PER BLOCK. If ANY row has a picture the column
// is drawn for EVERY row — a missing one gets the kit's silhouette plate — so
// the names stay on one left edge instead of stair-stepping around the holes.
// A block whose rows have no pictures at all draws no column, which is what
// keeps every table authored before this change byte-identical.

/**
 * One square picture in a leading column: `Image` when there is an address,
 * the kit's own graceful plate when there is not or when the network fails.
 *
 * Tappable when `onOpen` is given — a product photo is worth seeing large, and
 * ENLARGING IS THE HOST'S (`tapped {index, src}` → the surface's viewer). The
 * button is its own control rather than part of the row's button, because a
 * row that opens a shop and a picture that opens a lightbox are two different
 * answers to a tap, and a button inside a button is not markup.
 */
function Thumb({ src, alt, size = 56, index = 0, onOpen = null }) {
  const box = { flex: `0 0 ${size}px`, width: size, height: size };
  // NO ADDRESS → AN EMPTY BOX, not a plate. Both catalog entries seal it the
  // same way ("degrades to the text row" / "to the icon or to nothing, never a
  // broken image box, never a shifted column"): the column keeps its width so
  // the names stay on one edge, and it draws nothing of its own. A picture that
  // FAILS TO LOAD is the other case, and `Image` already owns it — its own
  // fallback plate, never a broken <img>.
  if (!src) return <div style={box} data-thumb="placeholder" aria-hidden />;
  const face = (
    <div style={{ width: size, height: size, borderRadius: 'var(--vi-r-inner)', overflow: 'hidden' }}>
      <Image props={{
        src, ratio: '1/1', fit: 'cover', rounded: false,
        alt: str(alt), fallbackKind: 'silhouette',
      }} />
    </div>
  );
  if (!onOpen) return <div style={box} data-thumb="image">{face}</div>;
  return (
    <button
      type="button"
      data-thumb="image"
      aria-label={str(alt) ? `Open picture of ${str(alt)}` : 'Open picture'}
      onClick={(e) => { e.stopPropagation(); onOpen({ index, src }); }}
      style={{ ...resetButton, ...box, cursor: 'zoom-in' }}
    >
      {face}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 1 · hero_media — the full-width result image
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The turn's headline picture — ONE image, the result.
 *
 * A comparison is `before_after`'s job, not this block's second career: a block
 * that doubles as both gets authored with only its BEFORE half filled (the
 * AFTER is still generating) and draws a hero whose one picture is missing.
 * The catalog (`registry.json` `props_schema`) and the model instruction
 * (`render/native-ir.md`) no longer mention `beforeImageUrl`.
 *
 * ⚠️ IT IS STILL READ HERE — a LEGACY READ. Web clients already have STORED
 * sessions carrying `hero_media` + `beforeImageUrl` (the privacy-conformance
 * fixtures are literal transcripts of them), and a session block is an
 * immutable snapshot: replaying an old turn must still draw both halves. This
 * branch retires with those sessions; do not delete it because the schema
 * stopped advertising the field.
 *
 * FULL BLEED IS THE HOST'S CALL, not the block's: only the surface knows
 * whether this is the first block of the turn, so it says so with
 * `variant: "bleed"` and the block drops its corners. Everything else is a
 * 12px-cornered picture like every other image on the surface.
 *
 * ⚠️ The bleed's negative margin cancels `--vi-gutter`, and the SESSION host no
 * longer draws one (`SessionBlockCard` is frameless and gutterless — the
 * surface's own reading column is the only inset). `variant` never reaches a
 * session block today (P2), but when it does the margin has to cancel the
 * SURFACE's gutter, not this token.
 *
 * ASYNC MEDIA IS FIRE-AND-CONTINUE: the model authors the block once and a
 * still-generating asset lands later through the `setSrc` slot. Until then
 * `Image` draws its own plate, which is the honest picture of "not yet".
 */
export function SessionHeroMedia({ variant, props = {}, slots, emit }) {
  const pushed = slots?.setSrc?.payload;
  const src = pushed?.src || props.imageUrl;
  const bleed = variant === 'bleed';
  const ratio = props.ratio || '4/3';
  const compare = Boolean(props.beforeImageUrl);
  // pending (`slot`, no `imageUrl`) / failed (`error`) — see `mediaWait`
  const wait = mediaWait(src, props);

  return (
    <div style={COL('var(--sp-2)')}>
      <div style={{
        borderRadius: bleed ? 0 : 'var(--vi-r-inner)',
        overflow: 'hidden',
        // the first hero bleeds edge to edge out of the surface's own gutter
        margin: bleed ? '0 calc(-1 * var(--vi-gutter))' : undefined,
      }}>
        {wait ? (
          <MediaWaitPlate
            state={wait} error={props.error} ratio={ratio} rounded={false} alt={props.title}
            stage={props.progressStage} pct={props.progressPct}
          />
        ) : compare ? (
          <BeforeAfterCompare props={{
            before: props.beforeImageUrl, after: src, ratio, reveal: 50, rounded: false,
          }} />
        ) : (
          <Image
            props={{ src, ratio, fit: 'cover', rounded: false, alt: props.title || '' }}
            emit={emit}
          />
        )}
      </div>
      {props.title ? <div style={{ ...T.h3, color: 'var(--vi-text)' }}>{props.title}</div> : null}
      {props.subtitle ? (
        <div style={{ ...T.small, color: 'var(--vi-text-muted)' }}>{props.subtitle}</div>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 15 · before_after — the dedicated comparison
// ═══════════════════════════════════════════════════════════════════════════

/**
 * TWO pictures side by side: the user's own input photo as the BEFORE, and
 * what this turn produced as the AFTER. This is the ONE block that may echo the
 * user's photo back, and it is why `hero_media` no longer can.
 *
 * THE TWO HALVES HAVE DIFFERENT CLOCKS, which is the whole reason this is its
 * own type. The BEFORE never waits — it is a ref the user already handed over.
 * The AFTER is fire-and-continue: authored ONCE as a pending `"slot":"mN"` with
 * NO `afterImageUrl`, filled in place when the generation lands (the api-server
 * writes it into `afterImageUrl`; see `session_wal_reducer._NATIVE_SLOT_REF_FIELD`).
 * So the pending / failed plate lives INSIDE the after half — a whole-block
 * plate would hide the before picture the user can already see, and would make a
 * half-finished comparison look like a block that has nothing in it.
 *
 * READY draws the kit's `BeforeAfterCompare` curtain — the SAME picture the
 * legacy `hero_media` compare drew, so an old stored turn and a new one read
 * identically on this surface. WAITING cannot: a curtain needs two pictures, so
 * it degrades to the two halves side by side with the plate in the after one.
 */
export function SessionBeforeAfter({ props = {}, slots }) {
  const pushed = slots?.setSrc?.payload;
  const after = pushed?.src || props.afterImageUrl;
  const ratio = props.ratio || '4/3';
  // pending (`slot`, no `afterImageUrl`) / failed (`error`) — see `mediaWait`
  const wait = mediaWait(after, props);

  const half = (label, face) => (
    <div style={{ position: 'relative', flex: '1 1 0', minWidth: 0 }}>
      {face}
      <span style={{
        ...T.label,
        position: 'absolute', left: 'var(--sp-2)', bottom: 'var(--sp-2)',
        padding: '2px 6px', borderRadius: 6,
        background: 'rgba(0,0,0,0.55)', color: '#fff',
      }}
      >
        {label}
      </span>
    </div>
  );

  return (
    <div style={{ ...CARD, ...COL('var(--sp-2)') }}>
      <div style={{ borderRadius: 'var(--vi-r-inner)', overflow: 'hidden' }}>
        {wait ? (
          <div style={{ display: 'flex', gap: 2 }}>
            {half('Before', (
              <Image
                props={{
                  src: props.beforeImageUrl, ratio, fit: 'cover', rounded: false, alt: 'Before',
                }}
              />
            ))}
            {half('After', (
              <MediaWaitPlate
                state={wait} error={props.error} ratio={ratio} rounded={false} alt={props.title}
                stage={props.progressStage} pct={props.progressPct}
              />
            ))}
          </div>
        ) : (
          <BeforeAfterCompare props={{
            before: props.beforeImageUrl, after, ratio, reveal: 50, rounded: false,
          }} />
        )}
      </div>
      {props.title ? <div style={{ ...T.h3, color: 'var(--vi-text)' }}>{props.title}</div> : null}
      {props.subtitle ? (
        <div style={{ ...T.small, color: 'var(--vi-text-muted)' }}>{props.subtitle}</div>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2 · text — a short titled paragraph
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The plainest structured block: the one a turn reaches for when the words
 * themselves are the result. SEALED: the body's own line breaks are preserved
 * — it is a paragraph the model wrote, not a heading it typed.
 */
export function SessionText({ props = {} }) {
  return (
    <div style={{ ...CARD, ...COL('var(--sp-2)') }}>
      <BlockTitle text={props.title} />
      <div style={{ ...T.body, color: 'var(--vi-text-secondary)', whiteSpace: 'pre-wrap' }}>
        {str(props.content)}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 3 · markdown — richer prose, and the catalog's degrade target
// ═══════════════════════════════════════════════════════════════════════════

/**
 * INLINE MARKS ONLY. A heading belongs in `title`, a list in `numbered_steps`,
 * a table in `table` — so this parses exactly four inline runs and nothing
 * block-level, which is also what stops model markup from becoming layout the
 * session design system never sanctioned.
 *
 * A LINK IS DRAWN, NOT FOLLOWED: `markdown` declares no signal, and leaving
 * the app is a host capability. So a link renders as marked text and the words
 * survive; a tappable link is what `link_preview` is for.
 */
const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function inlineRuns(text) {
  const out = [];
  for (const piece of str(text).split(INLINE_RE)) {
    if (!piece) continue;
    if (piece.startsWith('**') && piece.endsWith('**')) {
      out.push({ kind: 'strong', text: piece.slice(2, -2) });
    } else if (piece.startsWith('*') && piece.endsWith('*') && piece.length > 2) {
      out.push({ kind: 'em', text: piece.slice(1, -1) });
    } else if (piece.startsWith('`') && piece.endsWith('`') && piece.length > 2) {
      out.push({ kind: 'code', text: piece.slice(1, -1) });
    } else if (piece.startsWith('[')) {
      out.push({ kind: 'link', text: piece.slice(1, piece.indexOf(']')) });
    } else {
      out.push({ kind: 'plain', text: piece });
    }
  }
  return out;
}

function Inline({ text }) {
  return (
    <>
      {inlineRuns(text).map((run, i) => {
        if (run.kind === 'strong') return <strong key={i} style={{ fontWeight: 700 }}>{run.text}</strong>;
        if (run.kind === 'em') return <em key={i}>{run.text}</em>;
        if (run.kind === 'code') {
          return (
            <code key={i} style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--vi-t-sm)',
              background: 'var(--vi-surface-muted)', borderRadius: 'var(--r-pill)',
              padding: '1px 6px',
            }}>{run.text}</code>
          );
        }
        if (run.kind === 'link') {
          return (
            <span key={i} style={{ textDecoration: 'underline', textUnderlineOffset: 2 }}>{run.text}</span>
          );
        }
        return <span key={i}>{run.text}</span>;
      })}
    </>
  );
}

export function SessionMarkdown({ props = {} }) {
  const paragraphs = list(props.paragraphs).map(str).filter(Boolean);
  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      {paragraphs.map((p, i) => (
        <div key={i} style={{ ...T.body, color: 'var(--vi-text-secondary)', whiteSpace: 'pre-wrap' }}>
          <Inline text={p} />
        </div>
      ))}
      <BlockFooter text={props.footer} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 4 · agent_speech — a conversational line FROM the assistant
// ═══════════════════════════════════════════════════════════════════════════

/**
 * No card, no border, no shadow — that plate is what keeps "here is what I
 * found" from reading like part of the finding.
 */
export function SessionAgentSpeech({ props = {} }) {
  return (
    <div style={{
      ...T.body, color: 'var(--vi-text-secondary)',
      whiteSpace: 'pre-wrap', padding: 'var(--sp-1) 0',
    }}>
      {str(props.text)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 5 · stat_grid — two-column figure cells
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SEALED: the value renders EXACTLY as authored. `value` is a pre-formatted
 * string because the model, not the kit, owns rounding and locale — a kit that
 * re-formats a number it did not compute is a kit that can disagree with the
 * sentence next to it.
 */
export function SessionStatGrid({ props = {} }) {
  const items = list(props.items).filter((it) => it && (it.label || it.value));
  if (!items.length) return null;
  return (
    <div style={{ ...CARD, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
      {items.map((it, i) => (
        <div key={i} style={COL('var(--sp-1)')}>
          <div style={{ ...T.label, color: 'var(--vi-text-muted)' }}>{str(it.label)}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ ...T.h2, fontWeight: 800, color: 'var(--vi-text)' }}>{str(it.value)}</span>
            {it.unit ? (
              <span style={{ ...T.small, fontWeight: 650, color: 'var(--vi-text-muted)' }}>{str(it.unit)}</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 6 · table — a small key/value or two-column data table
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SEALED twice: an unknown row-icon name draws NOTHING (never a broken
 * glyph), and horizontal overflow stays inside the card — a wide table scrolls
 * in its own box and never scrolls the page sideways.
 *
 * `thumbnails` (B18 P-A) is an OPTIONAL leading picture column, one entry per
 * row (a string address, or `{url}`). It is drawn OUTSIDE the width algebra
 * below — a fixed 40px column that neither `lastIdx` nor `col()` knows about —
 * so a table authored without it renders byte-identically to before, which is
 * the whole back-compat requirement.
 */
export function SessionTable({ props = {}, emit }) {
  const rows = list(props.rows).map((r) => (Array.isArray(r) ? r : [r]));
  const headers = list(props.headers).map(str);
  const icons = list(props.icons);
  if (!rows.length) return null;
  const thumbOf = (r) => {
    const t = list(props.thumbnails)[r];
    return str(typeof t === 'string' ? t : t?.url);
  };
  const thumbs = rows.some((_, r) => thumbOf(r));
  const cell = {
    ...T.small, color: 'var(--vi-text-secondary)',
    padding: 'var(--sp-2) 0', textAlign: 'left', verticalAlign: 'top',
  };
  // What the LAST column holds decides the whole layout — twin of `_wideTable` in
  // the app kit (session_blocks.dart), same predicate, same two branches:
  //   * a SHORT value column (price / kcal / count) gets what it needs first: its
  //     own intrinsic width (`width: 1%` + nowrap collapses a table cell to its
  //     content), right-aligned, never wrapping; the columns before it keep the
  //     rest and are the ones allowed to wrap.
  //   * a LONG-TEXT column (a whole sentence per cell) gets NO priority: every
  //     column shares the width evenly and wraps. Keeping `nowrap` there would let
  //     one sentence set the table width and starve the front columns (the app bug
  //     where `AREA` collapsed to "Fo/re/he/ad").
  const lastIdx = Math.max(headers.length, ...rows.map((r) => r.length)) - 1;
  // CJK / full-width counts 2 — only ever used to answer "is this cell short?".
  const units = (s) => [...str(s)].reduce((n, ch) => n + (ch.codePointAt(0) > 0x2e7f ? 2 : 1), 0);
  const shortLast =
    units(headers[lastIdx] ?? '') <= 12 && rows.every((r) => units(r[lastIdx] ?? '') <= 12);
  const col = (i) => ({
    paddingRight: i === lastIdx ? 0 : 'var(--sp-3)',
    textAlign: shortLast && i === lastIdx ? 'right' : 'left',
    width: shortLast ? (i === lastIdx ? '1%' : undefined) : `${Math.floor(100 / (lastIdx + 1))}%`,
    whiteSpace: shortLast && (i === lastIdx || i === 0) ? 'nowrap' : 'normal',
  });
  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          {headers.length ? (
            <thead>
              <tr>
                {/* the picture column has no name — a header over it would be a
                    word the model never wrote */}
                {thumbs ? <th aria-hidden style={{ ...cell, width: 40, paddingRight: 'var(--sp-3)' }} /> : null}
                {headers.map((h, i) => (
                  <th key={i} style={{ ...cell, ...T.label, color: 'var(--vi-text-muted)', ...col(i) }}>{h}</th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {rows.map((row, r) => {
              const glyph = blockGlyph(icons[r]);
              return (
                <tr key={r} style={{ borderTop: r ? '0.7px solid var(--vi-border)' : undefined }}>
                  {thumbs ? (
                    <td style={{ ...cell, width: 40, paddingRight: 'var(--sp-3)' }}>
                      <Thumb
                        src={thumbOf(r)}
                        alt={str(row[0])}
                        size={40}
                        index={r}
                        onOpen={(payload) => emit?.('tapped', payload)}
                      />
                    </td>
                  ) : null}
                  {row.map((c, i) => (
                    <td
                      key={i}
                      style={{
                        ...cell,
                        fontWeight: i === 0 ? 650 : 400,
                        color: i === 0 ? 'var(--vi-text)' : 'var(--vi-text-secondary)',
                        ...col(i),
                      }}
                    >
                      {i === 0 && glyph ? (
                        <span aria-hidden style={{ marginRight: 6, color: 'var(--vi-text-muted)' }}>{glyph}</span>
                      ) : null}
                      {str(c)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <BlockFooter text={props.footer} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 7 · chip_list — tags / detected items
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SEALED: `selected` is an AUTHORED HIGHLIGHT — the ones that matched — and
 * never user state. A tap is a follow-up affordance, so it emits and changes
 * nothing: the row holds no selection, and nothing here outlives the turn.
 */
export function SessionChipList({ props = {}, emit }) {
  const chips = list(props.chips).map(str).filter(Boolean);
  if (!chips.length) return null;
  const selected = new Set(list(props.selected).map(Number));
  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
        {chips.map((chip, i) => {
          const on = selected.has(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => emit?.('activated', { index: i, value: chip })}
              style={{
                ...resetButton,
                ...T.smallStrong,
                padding: '6px 12px',
                borderRadius: 'var(--r-pill)',
                background: on ? 'var(--vi-accent)' : 'var(--vi-surface)',
                color: on ? 'var(--vi-accent-text)' : 'var(--vi-text-secondary)',
                border: on ? '0.7px solid var(--vi-accent)' : '0.7px solid var(--vi-border)',
              }}
            >
              {chip}
            </button>
          );
        })}
      </div>
      <BlockFooter text={props.footer} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 8 · icon_list — thumb + name + detail rows
// ═══════════════════════════════════════════════════════════════════════════

/** SEALED: a missing thumb degrades the ROW, never drops it — the name is the
 * content and the picture is decoration. */
export function SessionIconList({ props = {} }) {
  const items = list(props.items).filter((it) => it && (it.name || it.detail));
  if (!items.length) return null;
  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      <div style={COL('var(--sp-3)')}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
            {it.image ? (
              <div style={{ width: 34, flex: '0 0 34px', borderRadius: 9, overflow: 'hidden' }}>
                <Image props={{ src: it.image, ratio: '1/1', fit: 'cover', alt: str(it.name) }} />
              </div>
            ) : null}
            {/* `detail` is a SECOND LINE under the name, never a right-hand column:
                the schema has no value/trailing key, so a detail is as likely to be
                a sentence as a quantity — right-aligning it collapses the name and
                leaves a ragged edge. Twin of `buildViewIrSessionIconList`. */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...T.smallStrong, color: 'var(--vi-text)' }}>{str(it.name)}</div>
              {it.detail ? (
                <div style={{ ...T.small, color: 'var(--vi-text-muted)' }}>{str(it.detail)}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 9 · numbered_steps — ordered how-to steps
// ═══════════════════════════════════════════════════════════════════════════

/** SEALED: THE KIT OWNS THE NUMERALS. The ordinal is the index and is never
 * authored into the text, so a step can be inserted without renumbering
 * prose. */
export function SessionNumberedSteps({ props = {} }) {
  const steps = list(props.steps).map(str).filter(Boolean);
  if (!steps.length) return null;
  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      <div style={COL('var(--sp-3)')}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start' }}>
            <div style={{
              width: 26, height: 26, flex: '0 0 26px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--vi-surface-muted)',
              color: 'var(--vi-text)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 'var(--vi-t-sm)', fontWeight: 700,
            }}>{i + 1}</div>
            <div style={{ ...T.body, color: 'var(--vi-text-secondary)', flex: 1, minWidth: 0 }}>{step}</div>
          </div>
        ))}
      </div>
      <BlockFooter text={props.footer} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 10 · image_gallery — several result images in a scroll strip
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SEALED three ways: the HOST owns the full-screen viewer (the strip never
 * draws its own lightbox), `media://` is resolved server-side, and the strip
 * scrolls INSIDE the card. Late assets land per item through `setItems` —
 * fire-and-continue — so a half-generated gallery fills in place instead of
 * being re-emitted as a second card.
 */
export function SessionImageGallery({ props = {}, slots, emit }) {
  const pushed = slots?.setItems?.payload?.images;
  // a PENDING entry has `slot` and no `url` (a failed one has `error`): it is
  // kept, and drawn as the plate below, so the strip holds the place the fill
  // will land in rather than reflowing when it does
  const authored = list(pushed || props.images)
    .filter((im) => im && (typeof im === 'string' || im.url || im.slot || im.error));
  // …and a BLOCK-level slot is one the fill APPENDS as a new entry (registry:
  // `image_gallery` accepts it), so it too has a place before it lands
  const images = props.slot && !authored.some((im) => im && im.slot === props.slot)
    ? [...authored, { slot: props.slot, error: props.error }]
    : authored;
  if (!images.length) return null;
  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      <div style={{ display: 'flex', gap: 'var(--sp-2)', overflowX: 'auto', paddingBottom: 2 }}>
        {images.map((im, i) => {
          const src = typeof im === 'string' ? im : im.url;
          const wait = mediaWait(src, typeof im === 'string' ? {} : im);
          if (wait) {
            return (
              <div key={i} style={{ width: 148, flex: '0 0 148px' }}>
                {/* progress is per-ENTRY here: the reducer writes the marker on
                    the `images[]` item whose slot the tick named */}
                <MediaWaitPlate
                  state={wait} error={im.error} ratio="1/1" alt={str(im.alt)}
                  stage={im.progressStage} pct={im.progressPct}
                />
              </div>
            );
          }
          return (
            <button
              key={i}
              type="button"
              onClick={() => emit?.('tapped', { index: i, src })}
              style={{ ...resetButton, width: 148, flex: '0 0 148px' }}
            >
              <div style={{ borderRadius: 'var(--vi-r-inner)', overflow: 'hidden' }}>
                <Image props={{ src, ratio: '1/1', fit: 'cover', rounded: false, alt: str(im.alt) }} />
              </div>
              {im.caption ? (
                <div style={{ ...T.small, color: 'var(--vi-text-muted)', paddingTop: 6 }}>{str(im.caption)}</div>
              ) : null}
            </button>
          );
        })}
      </div>
      <BlockFooter text={props.footer} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 11 · link_preview — external link cards
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SEALED: the HOST owns leaving the app (a tap emits `activated {index,url}`
 * and the block never navigates), and A LINK WITH NO TITLE DEGRADES TO ITS
 * DOMAIN — a preview that failed to enrich is still a working link and not a
 * blank plate. A missing favicon draws the link glyph, never a hole.
 */
export function SessionLinkPreview({ props = {}, emit }) {
  const links = list(props.links).filter((l) => l && l.url);
  if (!links.length) return null;
  return (
    <div style={COL('var(--sp-2)')}>
      {links.map((link, i) => (
        <button
          key={i}
          type="button"
          onClick={() => emit?.('activated', { index: i, url: link.url })}
          style={{
            ...resetButton, ...ROW_CARD, ...hitTarget,
            display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', width: '100%',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, ...COL('var(--sp-1)') }}>
            <div style={{ ...T.smallStrong, color: 'var(--vi-text)' }}>
              {str(link.title) || str(link.domain) || str(link.url)}
            </div>
            {link.description ? (
              <div style={{
                ...T.small, color: 'var(--vi-text-secondary)',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>{str(link.description)}</div>
            ) : null}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {link.faviconUrl ? (
                <img
                  src={link.faviconUrl}
                  alt=""
                  width={14}
                  height={14}
                  style={{ borderRadius: 3, display: 'block' }}
                />
              ) : (
                <span aria-hidden style={{ color: 'var(--vi-text-muted)' }}>{blockGlyph('link')}</span>
              )}
              <span style={{ ...T.label, color: 'var(--vi-text-muted)' }}>
                {str(link.domain) || domainOf(link.url)}
              </span>
            </div>
          </div>
          {link.imageUrl ? (
            <div style={{ width: 64, flex: '0 0 64px', borderRadius: 'var(--vi-r-inner)', overflow: 'hidden' }}>
              <Image props={{ src: link.imageUrl, ratio: '1/1', fit: 'cover', rounded: false, alt: '' }} />
            </div>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** The host of a URL, for a link whose preview never carried a `domain`. */
function domainOf(url) {
  const m = /^[a-z]+:\/\/([^/?#]+)/i.exec(str(url));
  return m ? m[1].replace(/^www\./, '') : '';
}

// ═══════════════════════════════════════════════════════════════════════════
// 11b · location — a place on a map
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SEALED: the model supplies `lat/lng` (+ readable `label`/`address`) and, for the
 * MAP PICTURE, the `media://` handle `mcp_maps_static` returned for those same
 * coordinates — never a raw map URL or image bytes. That keeps the Google key behind
 * the MCP gateway (one audited, billed call per map) and lets the server resolve the
 * handle per client before the card ships. A tap opens the place in the maps app; on
 * the web playground that is a plain external link to Google Maps (the app renderer
 * routes it to `host.openMap` instead). No `mapUrl` — or one the server could not
 * resolve — degrades to the label + address: the place is still named.
 */
// Google Static Maps labels pins 1..9 then A..Z (see maps.handler `_MARKER_LABELS`); mirror
// that here so the list badge matches the pin on the map. Past 35, the pin is unlabelled.
const MARKER_LABELS = '123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const markerLabel = (i) => (i < MARKER_LABELS.length ? MARKER_LABELS[i] : '•');

export function SessionLocation({ props = {}, emit }) {
  const [mapOk, setMapOk] = useState(true);
  // A SET of places → one map with pins + a tappable list (multi-marker). The host draws the
  // pins; the map picture arrives by slot exactly like the single-point case.
  const markers = list(props.markers).filter((m) => m && Number.isFinite(Number(m.lat)) && Number.isFinite(Number(m.lng)));
  if (markers.length) {
    const mapUrl = str(props.mapUrl) || null;
    const mapWaiting = !mapUrl && !!str(props.slot) && !str(props.error);
    return (
      <div style={{ ...CARD, ...COL('var(--sp-2)'), padding: 0, overflow: 'hidden' }}>
        {mapUrl && mapOk ? (
          <div style={{ width: '100%', aspectRatio: '600 / 340', background: 'var(--vi-surface)' }}>
            <img src={mapUrl} alt="map" onError={() => setMapOk(false)} loading="lazy"
              style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ) : mapWaiting ? (
          <MediaWaitPlate state="pending" ratio="600 / 340" rounded={false} alt="Map" />
        ) : null}
        <div style={{ ...COL('var(--sp-1)'), padding: 'var(--sp-2)' }}>
          {markers.map((m, i) => {
            const mLabel = str(m.label);
            const mAddr = str(m.address);
            const mUrl = `https://www.google.com/maps/search/?api=1&query=${Number(m.lat)},${Number(m.lng)}`;
            return (
              <button key={i} type="button"
                onClick={() => emit?.('activated', { index: i, url: mUrl })}
                style={{ ...resetButton, ...hitTarget, display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: 'var(--sp-2)', cursor: 'pointer' }}>
                <div style={{ flex: '0 0 auto', width: 20, height: 20, borderRadius: '50%', background: 'var(--vi-accent, #d33)', color: '#fff', ...T.small, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{markerLabel(i)}</div>
                <div style={{ flex: 1, minWidth: 0, ...COL('2px') }}>
                  <div style={{ ...T.smallStrong, color: 'var(--vi-text)' }}>{mLabel || mAddr || `Place ${i + 1}`}</div>
                  {mLabel && mAddr ? <div style={{ ...T.small, color: 'var(--vi-text-secondary)' }}>{mAddr}</div> : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  const lat = Number(props.lat);
  const lng = Number(props.lng);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const label = str(props.label);
  const address = str(props.address);
  if (!hasCoords && !label && !address) return null;
  // The map picture is drawn by the HOST, fire-and-continue: the card lands carrying a
  // pending `slot` (the host is already fetching the map for these same coordinates via
  // `mcp_maps_static`, one audited + billed MCP call), and the `media://` handle fills
  // `mapUrl` by slot when it arrives. So there are three states — picture, still-waiting,
  // and none — and only the middle one holds a plate.
  const mapUrl = str(props.mapUrl) || null;
  // A map that never arrived degrades SILENTLY to label + address: unlike a picture the
  // user asked for, the map is our furniture, and "this one could not be generated" is
  // noise about a thing they never requested. The place is still named, which is the answer.
  const mapWaiting = !mapUrl && !!str(props.slot) && !str(props.error);
  // The renderer builds the maps deep-link from the coordinates (the model never
  // sends a URL); a tap hands it to the host's openUrl, exactly like link_preview.
  const openUrl = hasCoords
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : (label || address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label || address)}`
      : '');
  return (
    <button
      type="button"
      onClick={() => openUrl && emit?.('activated', { url: openUrl })}
      style={{ ...resetButton, ...CARD, ...COL('var(--sp-2)'), ...hitTarget, padding: 0, width: '100%', textAlign: 'left', overflow: 'hidden', cursor: openUrl ? 'pointer' : 'default' }}
    >
      {mapUrl && mapOk ? (
        <div style={{ width: '100%', aspectRatio: '600 / 340', background: 'var(--vi-surface)' }}>
          <img
            src={mapUrl}
            alt={label || address || 'map'}
            onError={() => setMapOk(false)}
            loading="lazy"
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      ) : mapWaiting ? (
        <MediaWaitPlate state="pending" ratio="600 / 340" rounded={false} alt="Map" />
      ) : null}
      {(label || address) ? (
        <div style={{ ...COL('var(--sp-1)'), padding: 'var(--sp-3)' }}>
          {label ? <div style={{ ...T.smallStrong, color: 'var(--vi-text)' }}>{`📍 ${label}`}</div> : null}
          {address ? <div style={{ ...T.small, color: 'var(--vi-text-secondary)' }}>{address}</div> : null}
        </div>
      ) : null}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 12 · shopping_list — a where-to-buy list
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SEALED twice: PRICES RENDER AS AUTHORED and the kit sums nothing — currency
 * and rounding are the model's, and a kit that adds up a column it did not
 * compute can print a total the answer never claimed. And only an item
 * carrying a `buyUrl` is tappable: a line of the answer is not a button.
 *
 * An item flagged `primary` (the runner's checkout card: "Review and pay") is
 * the card's one action: the ink pill reading "name · price", sending the same
 * `activated {index, url}`. A build that predates the flag draws it as a row
 * that opens all the same.
 */
export function SessionShoppingList({ props = {}, emit }) {
  const items = list(props.items).filter((it) => it && it.name);
  if (!items.length) return null;
  const isAction = (it) => Boolean(it.primary && it.buyUrl);
  // the leading picture column, drawn for every row as soon as ONE row has a
  // product photo — see the `Thumb` note above
  const thumbs = items.some((it) => !isAction(it) && str(it.image));
  const openImage = (payload) => emit?.('tapped', payload);
  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      <div style={COL('var(--sp-3)')}>
        {items.map((it, i) => {
          if (isAction(it)) return null;
          const words = (
            <div style={{ flex: 1, minWidth: 0, ...COL('2px'), textAlign: 'left' }}>
              <div style={{ ...T.smallStrong, color: 'var(--vi-text)' }}>{str(it.name)}</div>
              {it.brand || it.quantity ? (
                <div style={{ ...T.small, color: 'var(--vi-text-muted)' }}>
                  {[str(it.brand), str(it.quantity)].filter(Boolean).join(' · ')}
                </div>
              ) : null}
            </div>
          );
          return (
            <div
              key={i}
              style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start' }}
            >
              {thumbs ? (
                <Thumb src={str(it.image)} alt={str(it.name)} index={i} onOpen={openImage} />
              ) : null}
              {/* the WORDS are the shop link; the PICTURE is its own control */}
              {it.buyUrl ? (
                <button
                  type="button"
                  onClick={() => emit?.('activated', { index: i, url: it.buyUrl })}
                  style={{ ...resetButton, display: 'flex', flex: 1, minWidth: 0 }}
                >
                  {words}
                </button>
              ) : words}
              {it.price ? (
                <div style={{
                  ...T.body, fontWeight: 700, color: 'var(--vi-text)',
                  flex: '0 0 auto', whiteSpace: 'nowrap', textAlign: 'right',
                }}>{str(it.price)}</div>
              ) : null}
            </div>
          );
        })}
      </div>
      {props.totalEstimate ? (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          borderTop: '0.7px solid var(--vi-border)', paddingTop: 'var(--sp-3)',
        }}>
          <span style={{ ...T.label, color: 'var(--vi-text-muted)' }}>Total estimate</span>
          <span style={{ ...T.h3, color: 'var(--vi-text)' }}>{str(props.totalEstimate)}</span>
        </div>
      ) : null}
      {items.map((it, i) => (isAction(it) ? (
        <button
          key={`action-${i}`}
          type="button"
          onClick={() => emit?.('activated', { index: i, url: it.buyUrl })}
          style={ctaStyle(true)}
        >
          {[str(it.name), str(it.price)].filter(Boolean).join(' · ')}
        </button>
      ) : null))}
      <BlockFooter text={props.storeSuggestion} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 13 · video_player — a video result
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Poster + play, and a save affordance. It inherits `Video`'s seals rather
 * than restating them: sound starts OFF, a source that fails shows the poster
 * instead of a broken box, and nothing autoplays — the picture waits for the
 * press, which is also what reduced motion asks for.
 *
 * `saveRequested {url}` routes to `host.download`: writing a file to the
 * device is a HOST capability the block never re-implements.
 */
export function SessionVideoPlayer({ props = {}, slots, emit }) {
  const pushed = slots?.setSrc?.payload;
  const src = pushed?.src || props.url;
  const poster = pushed?.poster || props.poster;
  const [playing, setPlaying] = useState(false);
  // a video is generated the same way a picture is: `slot` until the ref lands,
  // `error` if it never will — and neither may offer a play button
  const wait = mediaWait(src, props);

  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }}>
      <BlockTitle text={props.title} />
      <div style={{ position: 'relative', borderRadius: 'var(--vi-r-inner)', overflow: 'hidden' }}>
        {wait ? (
          <MediaWaitPlate
            state={wait} error={props.error} ratio="16/9" rounded={false} alt={props.title}
            stage={props.progressStage} pct={props.progressPct}
          />
        ) : playing ? (
          <video
            src={src}
            poster={poster || undefined}
            controls
            muted
            playsInline
            autoPlay
            aria-label={props.title || 'video'}
            style={{ width: '100%', display: 'block', aspectRatio: '16 / 9', objectFit: 'cover', background: 'var(--vi-surface-muted)' }}
          />
        ) : (
          <>
            <Image props={{ src: poster, ratio: '16/9', fit: 'cover', rounded: false, alt: props.title || '' }} />
            <button
              type="button"
              aria-label="Play"
              onClick={() => { setPlaying(true); emit?.('tapped', { src }); }}
              style={{
                ...resetButton,
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <span style={{
                width: 52, height: 52, borderRadius: 'var(--r-pill)',
                background: 'var(--vi-scrim)', color: 'var(--vi-on-media)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 'var(--vi-t-lg)',
              }}>▶</span>
            </button>
            {props.duration ? (
              <span style={{
                position: 'absolute', right: 8, bottom: 8,
                ...T.label, color: 'var(--vi-on-media)', background: 'var(--vi-scrim)',
                borderRadius: 'var(--r-pill)', padding: '3px 8px',
              }}>{str(props.duration)}</span>
            ) : null}
          </>
        )}
      </div>
      {props.description ? (
        <div style={{ ...T.small, color: 'var(--vi-text-secondary)' }}>{str(props.description)}</div>
      ) : null}
      {/* nothing to save until the asset exists — an offer the host cannot
          honour is worse than no offer */}
      {src ? (
        <button
          type="button"
          onClick={() => emit?.('saveRequested', { url: src })}
          style={{
            ...resetButton, ...T.smallStrong, ...hitTarget,
            alignSelf: 'flex-start',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 'var(--r-pill)',
            border: '0.7px solid var(--vi-border)', color: 'var(--vi-text)',
          }}
        >
          <span aria-hidden>{blockGlyph('save')}</span>
          Save
        </button>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 16 · file_card — a document a worker produced
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The FILE result: a pdf / docx / xlsx / csv / zip a worker built and uploaded.
 *
 * IT IS MINTED AT DELIVERY, not authored ahead of time. A worker's output shape
 * is unknown at dispatch, so the model writes no slot for it — the reducer's
 * orphan rescue mints the module keyed by the ref when the file lands
 * (`session_wal_reducer._ORPHAN_BLOCK_TYPE_BY_KIND["document"]`), which means
 * this block arrives with `url` ALREADY SET. So it is NOT built around a
 * spinner: `loading` covers only the author-a-slot case the model is told not
 * to use, and the two states that matter are READY and FAILED.
 *
 * SEALED the same way `video_player` is: the HOST owns both verbs. A tap emits
 * `tapped {src}` (the host previews/opens it) and the save affordance emits
 * `saveRequested {url}` → `host.download {url}` — writing a file to the device
 * is a host capability the block never re-implements. And `url` is a resolved
 * address the api-server signed before the card shipped: `media://` never
 * reaches here, and the address itself is NEVER drawn as text.
 *
 * `url` MISSING IS A FAILED CARD, NEVER A BROKEN LINK. A file with no address
 * cannot be opened or saved, so the row stops being a control and says so —
 * the server's own sentence when it wrote one, else the plain fact.
 */
/** The pill every file action wears — Open and Save are the same shape. */
const ACTION_PILL = {
  ...resetButton, ...hitTarget,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 'var(--r-pill)',
  border: '0.7px solid var(--vi-border)', color: 'var(--vi-text)',
};

/**
 * FileTile — the ONE file presentation, shared by the `file_card` block and by
 * every `kind: "file"` deliverable a `worker_delegate` lands (B18 P-C: "same
 * look"). One card, three parts:
 *
 *   · a glyph TILE on a muted plate — the file's type, drawn not spelled;
 *   · the NAME on its own line with the type · size · pages line under it, and
 *     the model's description under that;
 *   · an ACTION ROW under a hairline: Open and Save, two equal pills.
 *
 * WHAT IT REPLACED, and why: a full-bleed row with a detached Save pill floating
 * below it, which read as two unrelated things and let a long file name push the
 * row past the reading column. The pills are now INSIDE the card that owns them,
 * and the name gets its own line to be long in.
 *
 * BOTH VERBS ARE THE HOST'S. Open emits `tapped {src}` and Save emits
 * `saveRequested {url}` — the block never navigates and never writes a file. The
 * card BODY is tappable too (the same `tapped`), because on a phone the file is
 * the tap target a thumb finds first; the explicit Open pill is what makes that
 * discoverable on a pointer.
 *
 * NOT READY = NO ACTIONS. A pending or failed tile keeps the card and the name
 * and drops every control: an Open button for a file with no address is a lie,
 * and the pending line is the honest thing to show instead.
 */
function FileTile({
  url, fileName, title, fileType, size, pageCount, description,
  state, error, progressStage, progressPct, onOpen, onSave,
}) {
  const name = str(fileName) || str(title) || 'File';
  const pages = typeof pageCount === 'number' && Number.isFinite(pageCount)
    ? `${Math.round(pageCount)} ${Math.round(pageCount) === 1 ? 'page' : 'pages'}`
    : '';
  const meta = state === 'failed'
    ? (str(error) || 'File unavailable')
    : [fileTypeLabel(fileType), str(size), pages].filter(Boolean).join(' · ');
  const line = state === 'pending'
    ? (mediaProgressLabel(progressStage, progressPct) || 'Generating…')
    : meta;

  const head = (
    <>
      <span
        aria-hidden
        style={{
          width: 44, height: 44, flex: '0 0 44px',
          borderRadius: 'var(--vi-r-inner)',
          background: 'var(--vi-surface-muted)', color: 'var(--vi-text-secondary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--vi-t-xl)',
        }}
      >
        {fileTypeGlyph(fileType)}
      </span>
      <div style={{ flex: 1, minWidth: 0, ...COL('3px'), textAlign: 'left' }}>
        <div style={{
          ...T.smallStrong, color: 'var(--vi-text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{name}</div>
        <div style={{ ...T.label, color: 'var(--vi-text-muted)' }}>{line}</div>
      </div>
    </>
  );
  const headStyle = { display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', width: '100%' };
  const ready = state === 'ready';

  return (
    <div
      data-file-card={state}
      data-media-state={ready ? undefined : state}
      data-media-stage={state === 'pending' ? (progressStage || undefined) : undefined}
      style={{
        border: '0.7px solid var(--vi-border)',
        borderRadius: 'var(--vi-r)',
        padding: 'var(--sp-3)',
        color: 'var(--vi-text)',
        overflow: 'hidden',
        ...COL('var(--sp-3)'),
      }}
    >
      {ready ? (
        <button type="button" onClick={() => onOpen?.(url)} style={{ ...resetButton, ...headStyle }}>
          {head}
        </button>
      ) : (
        <div style={headStyle}>{head}</div>
      )}
      {description ? (
        <div style={{ ...T.small, color: 'var(--vi-text-secondary)' }}>{str(description)}</div>
      ) : null}
      {ready ? (
        <div style={{
          display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap',
          borderTop: '0.7px solid var(--vi-border)', paddingTop: 'var(--sp-3)',
        }}>
          <button type="button" onClick={() => onOpen?.(url)} style={{ ...ACTION_PILL, ...T.smallStrong }}>
            <span aria-hidden>{blockGlyph('open')}</span>
            Open
          </button>
          <button type="button" onClick={() => onSave?.(url, name)} style={{ ...ACTION_PILL, ...T.smallStrong }}>
            <span aria-hidden>{blockGlyph('save')}</span>
            Save
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function SessionFileCard({ props = {}, slots, emit }) {
  const pushed = slots?.setSrc?.payload;
  const src = pushed?.src || props.url;
  // 'failed' (an `error` sentence) / 'pending' (a `slot`, the case the model is
  // told not to author) — and a card with NEITHER and no `url` is still a file
  // nothing can open, so it reads as failed rather than as a dead row.
  const wait = mediaWait(src, props);
  const state = src ? 'ready' : (wait || 'failed');
  return (
    <div style={CARD}>
      <FileTile
        url={src}
        fileName={props.fileName}
        title={props.title}
        fileType={props.fileType}
        size={props.size}
        pageCount={props.pageCount}
        description={props.description}
        state={state}
        error={props.error}
        progressStage={props.progressStage}
        progressPct={props.progressPct}
        onOpen={(u) => emit?.('tapped', { src: u })}
        onSave={(u, filename) => emit?.('saveRequested', { url: u, filename })}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 17 · worker_delegate — the work a delegate is doing, and what it delivers
// ═══════════════════════════════════════════════════════════════════════════

/** The in-flight ring — the kit's own (`RoleMediaForm`), in session ink. */
const DELEGATE_SPINNER = {
  width: 16, height: 16, flex: '0 0 16px', borderRadius: '50%',
  border: '2px solid var(--vi-border)', borderTopColor: 'var(--vi-text-muted)',
  animation: 'viNativeSpin 0.8s linear infinite',
};

/**
 * The module a `dispatch_worker` mints ON FIRE (B18 P-B), id-bound to that
 * worker exec by a host-stamped `wd-<execId>` slot, carrying a DELIVERY
 * CONTRACT: this card promises one or more assets and stays on screen until it
 * either has them or says why it does not.
 *
 * WHY IT EXISTS: before it, a worker-produced file appeared only at delivery
 * (rescue-minted `file_card`) — the whole wait was silent, and a failed worker
 * left nothing at all. This is the fire-and-continue shape §3 already uses for
 * pictures, applied to a delegate: fire → module + progress → fill or fail.
 *
 * FOUR STATES, AND NONE OF THEM SPINS FOREVER:
 *  · `pending` / `delivering` — the header carries a live ring and the stage
 *    line (`media_progress` ticks land as `progressStage`, exactly as they do on
 *    a pending picture). Deliverables that have ALREADY landed are drawn under
 *    it — a two-file worker shows the first file while the second uploads.
 *  · `done` — the ring goes, the deliverables stand on their own.
 *  · `failed` — the server's sentence, and whatever DID land is kept. A failed
 *    delegate never hides a file it already handed over.
 *
 * `done` WITH AN EMPTY `deliverables[]` IS A REAL OUTCOME, not an error and not
 * a wait: the worker answered in prose and had no file to give (the runner's
 * reconcile allows it explicitly). It draws a QUIET FINISHED LINE — the card was
 * on screen for the whole wait, so making it vanish at the end reads as a
 * glitch, and leaving a spinner would be the lie this module exists to remove.
 * The only card that draws nothing at all is one with nothing to name: no
 * deliverable, no title, no task.
 *
 * EVERY VERB IS THE HOST'S, as everywhere else on this surface: a file or an
 * image emits `tapped` (open / enlarge) and a file emits `saveRequested`; the
 * module downloads nothing and navigates nowhere. The payload is the catalog's
 * `{index,…}` and the SURFACE reads the deliverable's kind off the node, so the
 * signal never has to carry it.
 *
 * `error` IS PRINTED AS WRITTEN. The server sends a ready human sentence that
 * deliberately never names the worker or the machinery behind it (§6) — this
 * block adds no prefix of its own, and its own fallback wording is used only
 * when the sentence is missing entirely.
 *
 * ⚠️ `task` IS NEVER DRAWN. It is a worker-TIER identifier ("worker-standard")
 * — machinery, and §6 is that this surface does not say machinery out loud. It
 * is read for exactly one thing: as an EXISTENCE signal in the draw/don't-draw
 * gate below, so a finished delegate that has a task but no title still resolves
 * visibly instead of vanishing. The waiting line is the stage alone
 * ("Queued…" / "Generating…" / "Uploading…"), and the finished line is the
 * authored title or the neutral word — never the tier's name. (§8: the app kit
 * holds the same rule.)
 *
 * ⚠️ IT DOES NOT READ ITS `setDeliverables` SLOT, AND MUST NOT. The catalog
 * declares that slot to NAME the block's late-fill capability, not to offer a
 * transport: on the session lane a fill lands exactly one way — the reducer
 * writes `data.node.data` and the card re-renders (`_fill_native_block_slot`);
 * no backend path has ever pushed a slot payload to a session block, and a
 * verified grep of `api-server/app` finds none. Reading one anyway would be
 * worse than dead code: a slot inbox is CONTROLLER-level and outlives the node,
 * so a stale delivery could paint ONE worker's files onto ANOTHER worker's card
 * — and a delegate's identity changes every turn. Node re-render is the one
 * mechanism, which is also what keeps §8 parity with the app kit. The same is
 * true of `file_card.setSrc` and `image_gallery.setItems`.
 */
export function SessionWorkerDelegate({ props = {}, emit }) {
  const status = str(props.status).trim().toLowerCase() || 'pending';
  const terminal = status === 'done' || status === 'failed';
  const failed = status === 'failed';
  // NO `setDeliverables` INBOX READ, deliberately — see the note above.
  const items = list(props.deliverables).filter((d) => d && typeof d === 'object');
  const waiting = workerProgressLabel(props.progressStage, props.progressPct)
    || str(props.progressLabel)
    || 'Working…';
  const heading = str(props.title);
  // DONE WITH NOTHING is finished, not empty: the delegate says so in one quiet
  // line rather than vanishing off a surface it occupied for the whole wait.
  const finishedEmpty = status === 'done' && !items.length;
  // …and a card with nothing to name at all — no asset, no title, no task — has
  // no line to draw, so it draws none.
  if (finishedEmpty && !heading && !str(props.task)) return null;

  // the catalog's payloads exactly — `{index,src}` / `{index,url}`. The INDEX is
  // what makes them enough: the surface reads the deliverable's `kind` off the
  // node it is already holding, so a tap does not have to carry it.
  const open = (i, d) => emit?.('tapped', { index: i, src: str(d.url) });
  const save = (i, d) => emit?.('saveRequested', {
    index: i,
    url: str(d.url),
    filename: str(d.fileName) || str(d.title) || undefined,
  });

  return (
    <div style={{ ...CARD, ...COL('var(--sp-3)') }} data-delegate-status={status}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        {terminal ? null : <span aria-hidden data-delegate-spinner style={DELEGATE_SPINNER} />}
        {/* the finished-with-nothing card wears the kit's check where the ring
            was, so the row keeps its shape and reads as an outcome */}
        {finishedEmpty ? (
          <span aria-hidden style={{ color: 'var(--vi-text-muted)' }}>{blockGlyph('check')}</span>
        ) : null}
        <div style={{ flex: 1, minWidth: 0, ...COL('2px') }}>
          {heading ? <div style={{ ...T.h3, color: 'var(--vi-text)' }}>{heading}</div> : null}
          {/* the one line that changes while the work runs; on a terminal card
              it is the failure sentence, the finished word, or nothing */}
          {!terminal ? (
            <div style={{ ...T.label, color: 'var(--vi-text-muted)' }}>{waiting}</div>
          ) : failed ? (
            <div style={{ ...T.small, color: 'var(--vi-text-muted)' }}>
              {str(props.error) || 'That task could not be completed.'}
            </div>
          ) : finishedEmpty && !heading ? (
            // no title to carry the outcome → one neutral word. NEVER the bare
            // `task` label: "managed-standard" is machinery, and machinery is
            // exactly what this surface does not say out loud (§6).
            <div style={{ ...T.label, color: 'var(--vi-text-muted)' }}>Finished</div>
          ) : null}
        </div>
      </div>

      {items.length ? (
        <div style={COL('var(--sp-3)')}>
          {items.map((d, i) => {
            const kind = str(d.kind).trim().toLowerCase();
            const url = str(d.url);
            if (kind === 'image') {
              return (
                <div key={i} style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
                  <Thumb src={url} alt={str(d.caption)} size={84} index={i} onOpen={() => open(i, d)} />
                  {d.caption ? (
                    <div style={{ ...T.small, color: 'var(--vi-text-secondary)', minWidth: 0 }}>
                      {str(d.caption)}
                    </div>
                  ) : null}
                </div>
              );
            }
            if (kind === 'video') {
              // the video BLOCK is the one video presentation on this surface —
              // poster, one play, its own save — so a delegate reuses it whole
              return (
                <SessionVideoPlayer
                  key={i}
                  props={{ url, poster: str(d.posterUrl), title: str(d.caption) }}
                  emit={(signal, payload) => emit?.(signal, { index: i, ...payload })}
                />
              );
            }
            return (
              <FileTile
                key={i}
                url={url}
                fileName={str(d.fileName)}
                title={str(d.caption)}
                fileType={str(d.fileType)}
                description={kind === 'file' ? '' : str(d.caption)}
                state={url ? 'ready' : 'failed'}
                error={failed ? str(props.error) : ''}
                onOpen={() => open(i, d)}
                onSave={() => save(i, d)}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 14 · action_bar — the catalog's ONE interaction block
// ═══════════════════════════════════════════════════════════════════════════

/** The CTA shape the surface uses: the primary an ink pill, the rest outline buttons. */
function ctaStyle(primary) {
  return {
    ...resetButton, ...T.smallStrong, ...hitTarget,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '10px 18px', borderRadius: 'var(--r-pill)',
    background: primary ? 'var(--vi-accent)' : 'var(--vi-surface)',
    color: primary ? 'var(--vi-accent-text)' : 'var(--vi-text)',
    border: primary ? '0.7px solid var(--vi-accent)' : '0.7px solid var(--vi-border)',
  };
}

/**
 * SEALED three ways: a CLOSED action enum, an unrecognised action is DROPPED
 * rather than drawn dead (so a bar authored against a newer enum still renders
 * the buttons this build can honour), and it never confirms a write — logging
 * is the server's, and a bar may not claim one happened.
 *
 * The first honoured action is the primary (an ink pill); the rest are outline
 * buttons, which is the CTA shape the rest of the surface uses.
 */
export function SessionActionBar({ props = {}, emit }) {
  const actions = list(props.actions)
    .map((a, index) => ({ ...a, index }))
    .filter((a) => a && a.label && Object.prototype.hasOwnProperty.call(ACTION_HOST_OP, a.action));
  if (!actions.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
      {actions.map((a, i) => {
        const primary = i === 0;
        const glyph = blockGlyph(a.icon);
        return (
          <button
            key={a.index}
            type="button"
            onClick={() => emit?.('activated', { action: a.action, index: a.index, label: a.label })}
            style={ctaStyle(primary)}
          >
            {glyph ? <span aria-hidden>{glyph}</span> : null}
            {str(a.label)}
          </button>
        );
      })}
    </div>
  );
}
